<template>
  <!-- Never inside the instance in the frame (it would nest) nor in the detached planning window. -->
  <teleport v-if="!isEmbed && !isPopup" to="body">
    <lew-view-help-dialog v-if="isActive" />
  </teleport>
</template>

<script setup lang="ts">
/**
 * Where the per-view walkthrough ("Erklärung") is mounted — once, in `App.vue`. See docs/view-help.md.
 *
 * Everything it shows is `LewViewHelpDialog`, mounted only while a walkthrough is open. The player
 * itself is `composables/viewHelp/useViewHelp`, which the trigger in each view starts.
 */
import { computed, nextTick, watch } from "vue"
import { useRoute } from "vue-router"
import { useViewHelp } from "@/composables/viewHelp/useViewHelp"
import { IS_VIEW_HELP_EMBED } from "@/services/viewHelp/embed"

const route = useRoute()
const { isActive } = useViewHelp()

const isEmbed = IS_VIEW_HELP_EMBED
const isPopup = computed(() => route.query.popup === "true")

/**
 * Focus goes back to where it was when the walkthrough opened — the trigger, normally.
 *
 * Read before anything re-renders (a watcher runs ahead of the components), because the trigger is
 * disabled while the walkthrough is open, and a disabled button can lose focus. Given back after the
 * update, once the trigger is enabled again: `focus()` on a button that is still disabled does nothing,
 * which is what an earlier version did.
 */
let opener: HTMLElement | null = null
watch(isActive, active => {
  if (active) {
    opener = document.activeElement as HTMLElement | null
    return
  }
  const back = opener
  opener = null
  void nextTick(() => back?.focus?.())
})
</script>
